import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, FormControl, FormLabel, formLabelClasses, Grid, Input, Option, Select, Stack, Textarea, Typography } from '@mui/joy';
import offerApi from '../api/offer';
import { useLocation, useNavigate } from 'react-router-dom';
import { IOffer, OfferStatus, OfferType } from '../interface/IOffer';
import Breadcrumb from '../components/Breadcrumb';
import projectApi from '../api/project';
import { IProject } from '../interface/IProject';
import _ from 'lodash';
import { IOfferPosition } from '../interface/IOfferPosition';
import OfferPositionTable from '../components/OfferPositionTable';
import dayjs from 'dayjs';
import {
  calculateListPrice,
  calculateOfferPostionsTotalProjectPrice,
  calculateProjectPrice,
  daysToHours,
  deutschNumberFormatter,
  hoursToDays,
} from '../helper/utils';
import OfferPdf from '../components/OfferPdf';
import { PDFViewer } from '@react-pdf/renderer';
import rateApi from '../api/rate';
import clientApi from '../api/client';
import { IClient } from '../interface/IClient';
import { IInvoice } from '../interface/IInvoice';
import { useToast } from '../helper/ToastProvider';

type Props = {};

const OfferCreate = (props: Props) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerRate, setOfferRate] = useState<any>();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [offerId, setOfferId] = useState<string | null>(null);
  const [offerState, setOfferState] = useState<Partial<IOffer>>({
    offerCreateDate: dayjs().format('YYYY-MM-DD'),
    rateFrom: dayjs().format('YYYY-MM-DD'),
    validTo: dayjs().add(28, 'day').format('YYYY-MM-DD'),
    DEV: 0,
    PM: 0,
    discount: 0,
    openBudget: '0',
    status: 'draft' as OfferStatus,
  });
  const [offerPositions, setOfferPositions] = useState<Partial<IOfferPosition>[]>([]);
  const [invoices, setInvoices] = useState<any>();

  useEffect(() => {
    init();
  }, []);

  const getOfferRate = async (date: string) => {
    const { data } = await rateApi.getRateByDate(date);
    setOfferState((prev) => ({ ...prev, DEV: data.DEV, PM: data.PM }));
    setOfferRate(data);
    return data;
  };

  const _getAllProjects = async () => {
    const { data } = await projectApi.getAllProjects();
    setProjects(data);
  };

  const _getAllClients = async () => {
    const { data } = await clientApi.getAllClients();
    setClients(data);
  };

  const init = async () => {
    _getAllProjects();
    _getAllClients();
    const query = new URLSearchParams(location.search);
    const _offerId = query.get('offer');
    const _duplicate = query.get('duplicate');
    if (_offerId) {
      setOfferId(_offerId);
      try {
        const offer = (await offerApi.getOffer(_offerId)).data;
        if (!offer) {
          return navigate({ pathname: '/offers' });
        }
        let { deleted, offerPositions: _offerPositions, invoices: _invoices, ...temp } = offer;
        setInvoices(_invoices);
        setOfferState(temp);
        setOfferPositions(
          _offerPositions.map((op: any) => ({
            ...op,
            openBudget:
              op.projectPrice -
              _invoices.reduce((acc: any, invoice: IInvoice) => {
                const total = invoice.invoiceDetails.reduce((detailAcc: any, detail: any) => {
                  if (detail.offerPosition === op.position) {
                    return detailAcc + calculateProjectPrice(detail.timeInvoiced / 3600, offer[detail.rate], offer.discount);
                  }
                  return detailAcc;
                }, 0);
                return acc + total;
              }, 0),
          }))
        );
        setOfferRate({
          DEV: temp.DEV,
          PM: temp.PM,
        });
      } catch (e) {
        return navigate({ pathname: '/offers' });
      }
    } else if (_duplicate) {
      try {
        await getOfferRate(dayjs().format('YYYY-MM'));
        const offer = (await offerApi.getOffer(_duplicate)).data;
        if (!offer) {
          return navigate({ pathname: '/offers' });
        }
        let { projectId, clientId, title, type, discount, comment, offerValue, offerPositions: _offerPositions } = offer;
        const transformedData = _offerPositions.map(({ offerId, offerPositionId, openBudget, ...rest }: IOfferPosition) => ({
          ...rest,
          openBudget: rest.projectPrice,
        }));
        setOfferState((prev) => ({ ...prev, projectId, clientId, title, type, discount, comment, offerValue }));
        setOfferPositions(transformedData);
      } catch (e) {
        return navigate({ pathname: '/offers' });
      }
    } else {
      await getOfferRate(dayjs().format('YYYY-MM')); //change to date if id else today
    }
  };

  const changeOfferPosition = (key: string, value: any, index: number) => {
    const temp = [...offerPositions];
    if (key !== 'discount' && key !== 'rateFrom') {
      temp[index][key as keyof IOfferPosition] = value;
    }
    if (key === 'rate' || key === 'days' || key === 'hours') {
      if (key === 'days') {
        temp[index].hours = Number(daysToHours(value));
      } else if (key === 'hours') {
        temp[index].days = Number(hoursToDays(value));
      }
      if (offerRate) {
        temp[index].listPrice = Number(calculateListPrice(temp[index].hours || 0, offerState[temp[index].rate! as keyof IOffer] as any).toFixed(2));
        temp[index].projectPrice = Number(
          calculateProjectPrice(temp[index].hours || 0, offerState[temp[index].rate! as keyof IOffer] as any, offerState.discount!).toFixed(2)
        );
        if (offerState.status == 'accepted') {
          const invoicedPositionAmount = invoices
            ? invoices.reduce((acc: any, invoice: IInvoice) => {
                const total = invoice.invoiceDetails.reduce((detailAcc: any, detail: any) => {
                  if (detail.offerPosition === temp[index].position) {
                    return (
                      detailAcc +
                      calculateProjectPrice(detail.timeInvoiced / 3600, offerState[temp[index].rate! as keyof IOffer] as any, offerState.discount)
                    );
                  }
                  return detailAcc;
                }, 0);
                return acc + total;
              }, 0)
            : 0;
          temp[index].openBudget = (temp[index].projectPrice || 0) - invoicedPositionAmount;
        }
      }
      const totalPrice = Number(calculateOfferPostionsTotalProjectPrice(temp));
      setOfferState((prev) => ({ ...prev, offerValue: Number(totalPrice.toFixed(2)) }));
    }
    setOfferPositions(temp);
  };

  return (
    <>
      <Breadcrumb crumb={['Offers', offerId ? 'Update' : 'Create']} />
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level='h2' component='h1'>
          {offerId ? 'Update Offer' : 'Create Offer'}
        </Typography>
      </Box>
      <Box
        component='main'
        sx={{
          py: 2,
          pb: 5,
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          maxWidth: '100%',
          borderRadius: 'sm',
          '& form': {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          },
          [`& .${formLabelClasses.asterisk}`]: {
            visibility: 'hidden',
          },
        }}
      >
        <Stack gap={4} sx={{ mt: 2 }}>
          <form
            onSubmit={async (event: React.FormEvent<any>) => {
              event.preventDefault();
              try {
                const formData = new FormData(event.currentTarget);
                const formJson: IOffer = Object.fromEntries((formData as any).entries()) as IOffer;
                if (offerId) {
                  const updatedOffer = { ...formJson, offerPositions };
                  await offerApi.updateOffer(offerId, updatedOffer);
                  showToast({
                    message: 'Offer Update Success',
                    severity: 'success',
                  });
                } else {
                  const newOffer = { ...formJson, offerPositions };
                  await offerApi.createOffer(newOffer);
                  showToast({
                    message: 'Offer Create Success',
                    severity: 'success',
                  });
                }
                navigate('/offers');
              } catch (e) {
                console.error(e);
                showToast({
                  message: 'Offer Create/Update Failed',
                  severity: 'danger',
                });
              }
            }}
          >
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid container xs={8} md={8} spacing={2} sx={{ flexGrow: 1 }}>
                <Grid xs={12} md={12}>
                  <Divider sx={{ '--Divider-childPosition': `0%` }}>Project</Divider>
                </Grid>
                <Grid xs={6} md={6}>
                  <FormControl required>
                    <FormLabel>Project</FormLabel>
                    <Select
                      name='projectId'
                      required
                      value={(offerState?.projectId as number) || null}
                      onChange={(e, value) => {
                        setOfferState((prev) => ({ ...prev, projectId: value as number }));
                      }}
                    >
                      {projects &&
                        projects.map((project: IProject) => (
                          <Option key={project.projectId} value={project.projectId}>
                            {project.name}
                          </Option>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={6} md={6}>
                  <FormControl required>
                    <FormLabel>Client</FormLabel>
                    <Select
                      name='clientId'
                      required
                      value={(offerState?.clientId as number) || null}
                      onChange={(e, value) => {
                        setOfferState((prev) => ({ ...prev, clientId: value as number }));
                      }}
                    >
                      {clients &&
                        clients.map((client: IClient) => (
                          <Option key={client.clientId} value={client.clientId}>
                            {client.firstname} {client.lastname}
                          </Option>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} md={12}>
                  <Divider sx={{ '--Divider-childPosition': `0%` }}>Offer</Divider>
                </Grid>
                <Grid xs={12} md={12}>
                  <FormControl required>
                    <FormLabel>Offer Title</FormLabel>
                    <Input
                      type='text'
                      name='title'
                      value={offerState?.title || ''}
                      onChange={(e) => setOfferState((prev) => ({ ...prev, title: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={12}>
                  <FormControl required>
                    <FormLabel>Status</FormLabel>
                    <Select
                      name='status'
                      required
                      value={(offerState?.status as OfferStatus) || null}
                      onChange={(e, value) => {
                        setOfferState((prev) => ({ ...prev, status: value as OfferStatus }));
                      }}
                    >
                      {_.map(OfferStatus, (value, key) => (
                        <Option key={value} value={value}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* <Grid xs={12} md={6}>
                  <FormControl required>
                    <FormLabel>Contract Type</FormLabel>
                    <Select
                      name='type'
                      required
                      value={(offerState?.type as OfferType) || null}
                      onChange={(e, value) => {
                        setOfferState((prev) => ({ ...prev, type: value as OfferType }));
                      }}
                    >
                      {_.map(OfferType, (value, key) => (
                        <Option key={value} value={value}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid> */}
                <Grid container spacing={1} xs={12} md={6}>
                  <Grid xs={6} md={6}>
                    <FormControl required>
                      <FormLabel>Contract From</FormLabel>
                      <Input
                        name='offerTimeFrom'
                        type='date'
                        value={offerState?.offerTimeFrom || ''}
                        onChange={(e) => setOfferState((prev) => ({ ...prev, offerTimeFrom: e.target.value }))}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} md={6}>
                    <FormControl required>
                      <FormLabel>Contract To</FormLabel>
                      <Input
                        name='offerTimeTo'
                        type='date'
                        value={offerState?.offerTimeTo || ''}
                        onChange={(e) => setOfferState((prev) => ({ ...prev, offerTimeTo: e.target.value }))}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={1} xs={12} md={6}>
                  <Grid xs={6} md={6}>
                    <FormControl required>
                      <FormLabel>Create Date</FormLabel>
                      <Input
                        name='offerCreateDate'
                        type='date'
                        value={offerState?.offerCreateDate || ''}
                        onChange={(e) =>
                          setOfferState((prev) => ({
                            ...prev,
                            offerCreateDate: e.target.value,
                            validTo: dayjs(e.target.value).add(28, 'day').format('YYYY-MM-DD'),
                          }))
                        }
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} md={6}>
                    <FormControl required>
                      <FormLabel>Valid To</FormLabel>
                      <Input
                        name='validTo'
                        type='date'
                        value={offerState?.validTo || ''}
                        onChange={(e) => setOfferState((prev) => ({ ...prev, validTo: e.target.value }))}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={1} xs={12} md={6}>
                  <Grid xs={6} md={6}>
                    <FormControl required>
                      <FormLabel>Rate From</FormLabel>
                      <Input
                        name='rateFrom'
                        type='date'
                        value={offerState?.rateFrom || ''}
                        onChange={async (e) => {
                          const rateFrom = e.target.value;
                          setOfferState((prev) => ({ ...prev, rateFrom: rateFrom }));
                          const rate = await getOfferRate(dayjs(rateFrom).format('YYYY-MM'));
                          let temp = [...offerPositions];
                          temp = temp.map((t) => ({
                            ...t,
                            listPrice: Number(calculateListPrice(t.hours || 0, rate![t.rate!]).toFixed(2)),
                            projectPrice: Number(calculateProjectPrice(t.hours || 0, rate![t.rate!], offerState.discount!).toFixed(2)),
                          }));
                          const totalPrice = Number(calculateOfferPostionsTotalProjectPrice(temp));
                          setOfferState((prev) => ({ ...prev, offerValue: Number(totalPrice.toFixed(2)) }));
                          setOfferPositions(temp);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <FormControl>
                      <FormLabel>PM</FormLabel>
                      <Input name='PM' readOnly value={offerState?.PM || ''} />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <FormControl>
                      <FormLabel>DEV</FormLabel>
                      <Input name='DEV' readOnly value={offerState?.DEV || ''} />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={1} xs={12} md={6}>
                  <Grid xs={3} md={3}>
                    <FormControl>
                      <FormLabel>Open Budget</FormLabel>
                      <Input
                        readOnly
                        name='openBudget'
                        value={
                          offerState.status == 'accepted'
                            ? deutschNumberFormatter((offerState?.offerValue || 0) - (offerState.totalAmountInvoiced || 0))
                            : deutschNumberFormatter(0)
                        }
                        // onChange={(e) => setOfferState((prev) => ({ ...prev, openBudget: e.target.value as string }))}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={3} md={3}>
                    <FormControl>
                      <FormLabel>Discount</FormLabel>
                      <Input
                        type='number'
                        slotProps={{
                          input: {
                            className: 'num-input',
                            min: 0,
                          },
                        }}
                        name='discount'
                        value={offerState?.discount?.toString() || ''}
                        onChange={(e) => {
                          const disc = e.target.value;
                          setOfferState((prev) => ({ ...prev, discount: disc as any }));
                          let temp = [...offerPositions];
                          temp = temp.map((t) => ({
                            ...t,
                            projectPrice: Number(calculateProjectPrice(t.hours || 0, offerRate![t.rate!], Number(disc || 0)).toFixed(2)),
                          }));
                          const totalPrice = Number(calculateOfferPostionsTotalProjectPrice(temp));
                          setOfferState((prev) => ({ ...prev, offerValue: Number(totalPrice.toFixed(2)) }));
                          setOfferPositions(temp);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} md={6}>
                    <FormControl required>
                      <FormLabel>Offer Value</FormLabel>
                      <Input
                        type='text'
                        name='offerValue'
                        value={deutschNumberFormatter(offerState?.offerValue)}
                        disabled
                        // onChange={(e) => setOfferState((prev) => ({ ...prev, offerValue: Number(e.target.value) }))}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2} xs={4} md={4} sx={{ flexGrow: 1 }}>
                {offerState && offerPositions && (
                  <PDFViewer height='500' showToolbar={false} width='100%'>
                    <OfferPdf
                      offer={offerState}
                      offerPositions={offerPositions}
                      client={offerState.clientId ? clients && clients.find((c: IClient) => c.clientId === offerState.clientId) : null}
                    />
                  </PDFViewer>
                )}
              </Grid>
              <Grid xs={12} md={12}>
                <Divider sx={{ '--Divider-childPosition': `0%` }}>Offer Postitions</Divider>
              </Grid>
              <Grid xs={12} md={12}>
                <OfferPositionTable
                  status={offerState.status || 'draft'}
                  offerPositions={offerPositions}
                  onChange={changeOfferPosition}
                  onDelete={(index) => {
                    const temp = [...offerPositions];
                    temp.splice(index, 1);
                    for (let i = index; i < temp.length; i++) {
                      if (temp[i] && temp[i].position) {
                        temp[i].position = (temp[i].position || 1) - 1;
                      }
                    }
                    const totalPrice = Number(calculateOfferPostionsTotalProjectPrice(temp));
                    setOfferState((prev) => ({ ...prev, offerValue: Number(totalPrice.toFixed(2)) }));
                    setOfferPositions(temp);
                  }}
                />
              </Grid>
              <Grid xs={12} md={12} sx={{ alignContent: 'flex-end', justifyContent: 'right', display: 'flex' }}>
                <Button
                  size='sm'
                  onClick={() => {
                    const temp = [...offerPositions];
                    temp.push({
                      position: temp.length + 1,
                      rate: 'DEV',
                      subject: '',
                      listPrice: 0,
                      projectPrice: 0,
                      days: 0,
                      hours: 0,
                      openBudget: 0,
                      contractType: OfferType.PERIODICEXPENSES,
                      description: null,
                    });
                    setOfferPositions(temp);
                  }}
                >
                  Add
                </Button>
              </Grid>
              <Grid xs={12} md={12}>
                <Divider sx={{ '--Divider-childPosition': `0%` }}>Comment</Divider>
              </Grid>
              <Grid xs={12} md={12}>
                <FormControl>
                  <Textarea
                    name='comment'
                    value={offerState?.comment || ''}
                    minRows={3}
                    onChange={(e) => setOfferState((prev) => ({ ...prev, comment: e.target.value as string }))}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Stack gap={4} sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Button type='submit' sx={{ marginRight: 2 }}>
                  {offerId ? 'Update' : 'Create'}
                </Button>
                <Button onClick={() => navigate('/offers')}>Cancel</Button>
              </Box>
            </Stack>
          </form>
        </Stack>
        {/* <Stack gap={4} sx={{ mt: 2, width: '35%' }}>
          <PDFViewer height={800} showToolbar={false} width='100%'>
            <OfferPdf
              offer={offerState}
              offerPositions={offerPositions}
              client={offerState.clientId ? clients && clients.find((c: IClient) => c.clientId == offerState.clientId) : null}
            />
          </PDFViewer>
        </Stack> */}
      </Box>
    </>
  );
};

export default OfferCreate;
